import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {string | undefined} */
  #lastChangedOptionValueId;

  /** @type {number} */
  #fetchRequestId = 0;

  /** @type {(event: Event) => void} */
  #onVariantChange = this.variantChanged.bind(this);

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.#onVariantChange);
    this.addEventListener('click', this.#handleCustomSelectClick.bind(this));
  }

  /**
   * Handles custom dropdown option clicks.
   * @param {Event} event
   */
  #handleCustomSelectClick(event) {
    if (!(event.target instanceof Element)) return;

    const optionButton = event.target.closest('[data-custom-select-option]');

    if (!(optionButton instanceof HTMLButtonElement) || optionButton.disabled) return;

    const customSelect = optionButton.closest('.variant-option__custom-select');
    const select = customSelect?.querySelector('select.variant-option__select');

    if (!(select instanceof HTMLSelectElement) || !optionButton.dataset.value) return;

    event.preventDefault();

    const optionValueId = optionButton.dataset.optionValueId;

    for (const option of select.options) {
      const isSelected = option.dataset.optionValueId === optionValueId;
      option.selected = isSelected;
      option.toggleAttribute('selected', isSelected);
    }

    if (optionValueId) {
      const selectedOption = Array.from(select.options).find(
        (option) => option.dataset.optionValueId === optionValueId
      );
      if (selectedOption) {
        select.value = selectedOption.value;
      }
    }

    this.#syncCustomSelectFromSelect(select);

    if (customSelect instanceof HTMLDetailsElement) {
      customSelect.open = false;
    }

    // Mirror a native <select> change — same path color swatch radios use via the change listener.
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Syncs the custom dropdown UI with its hidden select element.
   * @param {HTMLSelectElement} select
   */
  #syncCustomSelectFromSelect(select) {
    const customSelect = select.closest('.variant-option__custom-select');
    const selectedOption = select.options[select.selectedIndex];
    const valueElement = customSelect?.querySelector('.variant-option__custom-select-value');
    const optionButtons = customSelect?.querySelectorAll('[data-custom-select-option]');

    if (valueElement && selectedOption) {
      valueElement.textContent = selectedOption.textContent?.trim() ?? selectedOption.value;
    }

    optionButtons?.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isSelected = button.dataset.value === select.value;

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.#lastChangedOptionValueId = selectedOption.dataset.optionValueId;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.selected = option === newSelectedOption;

        if (option === newSelectedOption) {
          option.setAttribute('selected', 'selected');
        } else {
          option.removeAttribute('selected');
        }
      }

      this.#syncCustomSelectFromSelect(target);
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestId = ++this.#fetchRequestId;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== this.#fetchRequestId) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          const variantData = JSON.parse(textContent);
          const sourceId = this.#lastChangedOptionValueId ?? this.selectedOptionId ?? String(variantData.id ?? '');

          // We grab the variant object from the response and dispatch an event with it.
          if (sourceId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, sourceId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Returns the variant picker form, falling back to the component root for card swatches.
   * @returns {Element}
   */
  #getOptionsRoot() {
    return this.querySelector('.variant-picker__form') ?? this;
  }

  /**
   * Reads selected option value IDs in product option order (matches the form layout).
   * @returns {string[]}
   */
  #getSelectedOptionsValuesInOrder() {
    const root = this.#getOptionsRoot();

    /** @type {string[]} */
    const values = [];

    for (const child of root.children) {
      if (!(child instanceof HTMLElement)) continue;

      if (child.matches('fieldset.variant-option')) {
        const checked = child.querySelector('input[type="radio"]:checked');
        const { optionValueId } = checked?.dataset ?? {};

        if (!optionValueId) throw new Error('No option value ID found');

        values.push(optionValueId);
        continue;
      }

      if (child.matches('.variant-option--dropdowns')) {
        const select = child.querySelector('select.variant-option__select');

        if (!(select instanceof HTMLSelectElement)) {
          throw new Error('No option value ID found');
        }

        const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex];
        const { optionValueId } = selected?.dataset ?? {};

        if (!optionValueId) throw new Error('No option value ID found');

        values.push(optionValueId);
      }
    }

    // Fallback for nested structures (e.g. overflow-list wrappers) that are not direct form children.
    if (values.length === 0) {
      for (const option of this.querySelectorAll('.variant-option')) {
        if (option instanceof HTMLFieldSetElement) {
          const checked = option.querySelector('input[type="radio"]:checked');
          const { optionValueId } = checked?.dataset ?? {};
          if (optionValueId) values.push(optionValueId);
        } else {
          const select = option.querySelector('select.variant-option__select');
          if (!(select instanceof HTMLSelectElement)) continue;
          const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex];
          const { optionValueId } = selected?.dataset ?? {};
          if (optionValueId) values.push(optionValueId);
        }
      }
    }

    return values;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const root = this.#getOptionsRoot();

    for (const child of root.children) {
      if (!(child instanceof HTMLElement)) continue;

      if (child.matches('fieldset.variant-option')) {
        const checked = child.querySelector('input[type="radio"]:checked');
        if (checked instanceof HTMLInputElement) return checked;
      } else if (child.matches('.variant-option--dropdowns')) {
        const select = child.querySelector('select.variant-option__select');
        if (select instanceof HTMLSelectElement) {
          const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex];
          if (selected instanceof HTMLOptionElement) return selected;
        }
      }
    }

    // Fallback for nested fieldsets (card swatches / overflow-list).
    for (const option of this.querySelectorAll('.variant-option')) {
      if (option instanceof HTMLFieldSetElement) {
        const checked = option.querySelector('input[type="radio"]:checked');
        if (checked instanceof HTMLInputElement) return checked;
      } else {
        const select = option.querySelector('select.variant-option__select');
        if (select instanceof HTMLSelectElement) {
          const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex];
          if (selected instanceof HTMLOptionElement) return selected;
        }
      }
    }

    return undefined;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values in product option order.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    return this.#getSelectedOptionsValuesInOrder();
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
